import { useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level cache so multiple components share a single fetch per session.
let cachedContent = null;
let pendingPromise = null;
const subscribers = new Set();

/** Read any __preview overrides from the URL. Format:
 *  ?__preview={"landing.hero.cta_text":"My new text"}   (URL-encoded JSON) */
function readPreviewOverrides() {
  if (typeof window === "undefined") return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("__preview");
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

async function fetchSiteContent() {
  if (cachedContent) return cachedContent;
  if (pendingPromise) return pendingPromise;
  pendingPromise = fetch(`${API_URL}/api/site-content`)
    .then((r) => (r.ok ? r.json() : {}))
    .then((data) => {
      // Merge URL-based preview overrides on top of saved content
      cachedContent = { ...(data || {}), ...readPreviewOverrides() };
      subscribers.forEach((cb) => cb(cachedContent));
      pendingPromise = null;
      return cachedContent;
    })
    .catch(() => {
      pendingPromise = null;
      cachedContent = { ...readPreviewOverrides() };
      return cachedContent;
    });
  return pendingPromise;
}

/**
 * React hook: returns the site content dict { key: value } once loaded.
 * Until loaded, returns an empty object so callers should always supply
 * fallback text (e.g. `content[key] || "Default copy"`).
 */
export function useSiteContent() {
  const [content, setContent] = useState(cachedContent || {});

  useEffect(() => {
    let active = true;
    const handler = (data) => {
      if (active) setContent(data);
    };
    subscribers.add(handler);
    if (!cachedContent) {
      fetchSiteContent().then((data) => {
        if (active) setContent(data);
      });
    }
    return () => {
      active = false;
      subscribers.delete(handler);
    };
  }, []);

  return content;
}

/**
 * Returns a single content value by key, using fallback while loading or missing.
 */
export function useSiteText(key, fallback = "") {
  const content = useSiteContent();
  return content[key] || fallback;
}

/** Force a refetch after admin edits so all pages update without reload. */
export function invalidateSiteContent() {
  cachedContent = null;
  return fetchSiteContent();
}

/** Split a long textarea value (paragraphs separated by blank lines) into an array.
 *  Use in JSX: {paragraphsFrom(t("key", default)).map((p,i) => <p key={i}>{p}</p>)} */
export function paragraphsFrom(text) {
  if (!text) return [];
  return text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
}
